import React, { useCallback, useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { Box, CircularProgress, Divider, Grid, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import StepperVertical from '../../components/Stepper/StepperVertical';
import { useAppSelector } from '../../hooks/useRedux';
import GridLabels from '../VehicleTabs/GridLabels';
import TabLoader from '../VehicleTabs/TabLoader';
import { getVehicleOrder } from '../../services/order';
import useMetaData from '../../hooks/useMetaData';
import { META_DATA_MODULES, META_DATA_SUB_MODULES } from '../../utils/constants';
import { jsonPathToValue } from '../../utils/json';
import { lifeCycleSnapshot, vehicleService } from '../../services/vehicles';
import { contentType, handleContent } from '../Reservation';
import { stagePriority } from '../VehicleStageGraph';

const ScheduleDelivery = () => {
  //  use hooks
  const { reservationId } = useParams<{ reservationId: string }>();
  const metaData = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.ScheduleDelivery);
  const lifecycleMeta = useMetaData(META_DATA_MODULES.v360Page, META_DATA_SUB_MODULES.LifeCycleSnapShot);

  // global states
  const { currentStage } = useAppSelector((state) => state.vehicle);

  // local states
  const [deliveryData, setDeliveryData] = useState<any>(null);
  const [finalDelivery, setFinalDelivery] = useState<any>(null);

  // constants
  const stepsVertical = deliveryData?.stageInformation
    ?.sort((a: any, b: any) => parseFloat(b?.sequence) - parseFloat(a?.sequence))
    ?.filter(
      (fil: { ruleResult: { isCompleted: boolean }; subStage: string; showSubStage: boolean }) => fil?.showSubStage
    )
    ?.map((item: any) => {
      return {
        id: '1',
        label: (
          <Typography variant="body2" fontWeight={600}>
            {lifecycleMeta?.fields?.[item?.subStage]?.caption}
          </Typography>
        ),
        content: <>{handleContent(item?.ruleResult, lifecycleMeta?.fields?.[item?.subStage])}</>,
        isCompleted: item?.ruleResult?.isCompleted,
      };
    });

  const deliveryFields = useMemo(
    () =>
      finalDelivery
        ? metaData?.data?.deliveryDetails?.rows?.map((row: any) => {
            const value = jsonPathToValue(finalDelivery, row.id) || '';
            return {
              label: row?.name,
              title: value || '--',
              type: row.type,
              format: row?.format,
            };
          }) || []
        : [],
    [finalDelivery, metaData]
  );

  // fetching record
  const fetchDeliveryData = useCallback(async () => {
    try {
      const resp = await lifeCycleSnapshot({
        method: 'POST',
        data: {
          reservationId,
          stages: ['Delivery'],
          langCode: 'en-us',
        },
      });
      setDeliveryData(resp?.data?.data?.[0]);
    } catch (error) {
      console.log(error);
    }
  }, [reservationId]);

  // fetching final Delivery data
  const fetchFinalDelivery = useCallback(async () => {
    try {
      const resp = await vehicleService({ method: 'GET', url: `${reservationId}/finalDelivery` });
      setFinalDelivery(resp?.data?.data);
    } catch (error) {
      console.log(error);
    }
  }, [reservationId]);

  // lifecycle hook
  useEffect(() => {
    if (reservationId) {
      fetchDeliveryData();
      fetchFinalDelivery();
    }
  }, [reservationId, fetchDeliveryData, fetchFinalDelivery]);

  if (!deliveryData)
    return (
      <Box sx={{ position: 'relative', minHeight: '250px' }}>
        <CircularProgress
          color="inherit"
          size={18}
          sx={{ position: 'absolute', left: '0', right: '0', top: '50%', margin: '0 auto' }}
        />
      </Box>
    );
  return (
    <Box>
      <Typography fontSize={20} mb={1.2} fontWeight={500} lineHeight="24px">
        {metaData?.data?.heading}
      </Typography>
      <Grid item xs={12} mb={1}>
        <Divider sx={{ borderColor: '#E9E9E9' }} />
      </Grid>
      <Box sx={{ width: '100%', my: 4 }}>
        <Typography fontSize={14} mb={1} fontWeight={500}>
          {metaData?.data?.deliveryDetails?.heading}
        </Typography>
        <Divider sx={{ borderColor: '#e3e3e3', borderStyle: 'dashed' }} />
        <Grid container spacing={2} mt={0}>
          <GridLabels
            labels={deliveryFields}
            labelSx={{ fontSize: '14px', fontWeight: '400', color: '#717171' }}
            titleSx={{ fontSize: '16px', fontWeight: '500', color: '#3E3E3E' }}
          />
        </Grid>
      </Box>
      {stagePriority?.[currentStage] >= 7 ? (
        <StepperVertical steps={stepsVertical} activeStep={stepsVertical?.length ?? 10} />
      ) : null}
    </Box>
  );
};
export default ScheduleDelivery;
